import { TrendingUp, TrendingDown, ArrowUpRight } from 'lucide-react';

// Accent roles map onto the single brand green; only semantic states
// (warning/danger) break away from it.
const toneStyles = {
  brand: 'bg-primary-50 text-primary-900 ring-1 ring-primary-100 dark:bg-primary-950/70 dark:text-primary-300 dark:ring-primary-900',
  neutral: 'bg-surface-100 text-surface-600 ring-1 ring-surface-200/70 dark:bg-surface-800 dark:text-surface-300 dark:ring-surface-700',
  warning: 'bg-warning-50 text-warning-700 ring-1 ring-warning-100 dark:bg-warning-900/25 dark:text-warning-400 dark:ring-warning-900/60',
  danger: 'bg-danger-50 text-danger-700 ring-1 ring-danger-100 dark:bg-danger-900/25 dark:text-danger-400 dark:ring-danger-900/60',
};

const bgColors = {
  blue: toneStyles.brand,
  green: toneStyles.brand,
  indigo: toneStyles.brand,
  teal: toneStyles.brand,
  purple: toneStyles.brand,
  amber: toneStyles.warning,
  orange: toneStyles.warning,
  red: toneStyles.danger,
  slate: toneStyles.neutral,
};

export default function StatCard({
  title,
  value,
  icon: Icon,
  color = 'blue',
  trend,
  trendValue,
  subtitle,
  onClick,
  className = ''
}) {
  const interactive = Boolean(onClick);
  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      className={`stat-card group flex-col gap-0 ${interactive ? 'cursor-pointer hover:-translate-y-0.5 hover:border-surface-300 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-[#60a5fa] dark:focus-visible:ring-offset-surface-950 dark:hover:border-surface-700' : ''} ${className}`}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div className={`stat-icon ${bgColors[color] || bgColors.brand}`}>
          <Icon size={20} />
        </div>
        {interactive && (
          <ArrowUpRight
            size={14}
            className="mt-1 flex-shrink-0 text-surface-300 transition-colors group-hover:text-primary-800 dark:text-surface-600 dark:group-hover:text-primary-400"
            aria-hidden="true"
          />
        )}
      </div>

      <p className="mt-4 min-h-[2rem] text-[11px] font-medium uppercase leading-snug tracking-[0.1em] text-surface-500 dark:text-surface-400">
        {title}
      </p>
      <p className="mt-1 break-words text-xl font-medium leading-tight tracking-[-0.3px] text-surface-900 tabular-nums dark:text-white">
        {value}
      </p>

      {trend != null && (
        <div className={`mt-2 flex flex-wrap items-center gap-x-1 text-xs font-medium ${
          trendValue === 0
            ? 'text-surface-400 dark:text-surface-500'
            : trend === 'up' ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
        }`}>
          {trendValue !== 0 && (trend === 'up' ? <TrendingUp size={12} aria-hidden="true" /> : <TrendingDown size={12} aria-hidden="true" />)}
          <span className="tabular-nums">{trendValue === 0 ? '' : trend === 'up' ? '+' : ''}{trendValue}%</span>
          {subtitle && <span className="font-normal text-surface-400 dark:text-surface-500">vs periode lalu</span>}
        </div>
      )}
      {subtitle && !trend && (
        <p className="mt-2 truncate text-xs text-surface-400 dark:text-surface-500">{subtitle}</p>
      )}
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="card p-5">
      <div className="skeleton h-10 w-10 rounded-xl" />
      <div className="mt-4 space-y-2.5">
        <div className="skeleton h-2.5 w-20" />
        <div className="skeleton h-6 w-32" />
        <div className="skeleton h-2.5 w-24" />
      </div>
    </div>
  );
}
